import { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '@context/AppContext';
import { useChallenge } from './useChallenge';
import GameHUD from '../shared/GameHUD';
import { Check } from 'lucide-react';

/**
 * Draw-Off phase — only tied players draw, others spectate.
 * Fixed 60-second timer. Same submit → vote → results flow after.
 * Second tie = shared win.
 */
export default function DrawOff({ stageRef }) {
  const { state, dispatch } = useApp();
  const { game, user, canvas } = state;
  const { submitEntry, advanceToReview } = useChallenge();
  const [submitted, setSubmitted] = useState(false);
  const initialisedRef = useRef(false);

  const isEligible = (game.drawoffEligible || []).includes(user.id);

  // On mount: clear canvas for eligible drawers, enter solo mode
  useEffect(() => {
    if (initialisedRef.current) return;
    initialisedRef.current = true;

    if (isEligible) {
      dispatch({ type: 'CLEAR_ALL_STROKES' });
      if (!canvas.soloMode) {
        dispatch({ type: 'TOGGLE_SOLO_MODE' });
      }
    }
  }, [dispatch, isEligible, canvas.soloMode]);

  const handleSubmit = useCallback(async () => {
    if (submitted || !isEligible) return;
    setSubmitted(true);
    await submitEntry(stageRef);
  }, [submitted, isEligible, submitEntry, stageRef]);

  const handleTimerExpire = useCallback(async () => {
    if (!submitted && isEligible) {
      setSubmitted(true);
      await submitEntry(stageRef);
    }
    if (user.isHost) {
      setTimeout(() => advanceToReview(), 1500);
    }
  }, [submitted, isEligible, submitEntry, stageRef, user.isHost, advanceToReview]);

  // Check if all eligible players have submitted
  const submissions = game.submissions || {};
  const eligible = game.drawoffEligible || [];
  const allSubmitted = eligible.every(id => submissions[id]);

  useEffect(() => {
    if (user.isHost && eligible.length > 0 && allSubmitted) {
      setTimeout(() => advanceToReview(), 1000);
    }
  }, [user.isHost, eligible.length, allSubmitted, advanceToReview]);

  return (
    <GameHUD
      timerEndsAt={game.timerEndsAt}
      timerDuration={60}
      onTimerExpire={handleTimerExpire}
      emoji="⚔️"
      label="Draw-Off!"
      sublabel={isEligible ? game.challenge?.text : 'Watch the draw-off...'}
    >
      {isEligible ? (
        <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg border border-slate-100 flex items-center gap-4">
          {!submitted ? (
            <button
              onClick={handleSubmit}
              className="flex items-center gap-2 px-5 py-2 rounded-lg bg-red-500 text-white text-sm font-semibold font-display hover:bg-red-600 transition-colors"
            >
              <Check className="w-4 h-4" />
              Submit
            </button>
          ) : (
            <p className="text-sm text-slate-500 font-display">Submitted! Waiting...</p>
          )}
        </div>
      ) : (
        <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg border border-slate-100">
          <p className="text-sm text-slate-500 font-display">
            Watching the draw-off...
          </p>
        </div>
      )}
    </GameHUD>
  );
}
